import {
  type PayloadAction,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import type { RootState } from '../index';
import { fetchNotificationsFromDB } from '../thunks/notificationThunks';

// Types
export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'request' | 'payment' | 'document' | 'message';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  userId: string;
  actionUrl?: string;
  actionText?: string;
  metadata?: Record<string, unknown>;
  priority: 'low' | 'medium' | 'high';
}

interface NotificationsState {
  loading: boolean;
  error: string | null;
  unreadCount: number;
  lastFetch: string | null;
  soundEnabled: boolean;
  desktopEnabled: boolean;
}

// Entity adapter for normalized state
const notificationsAdapter = createEntityAdapter<Notification>({
  selectId: (notification) => notification.id,
  sortComparer: (a, b) => b.timestamp.localeCompare(a.timestamp),
});

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (userId: string) => {
    const response = await fetch(`/api/users/${userId}/notifications`);
    if (!response.ok) throw new Error('Failed to fetch notifications');
    return response.json();
  }
);

export const markAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: string) => {
    const response = await fetch(`/api/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
    if (!response.ok) throw new Error('Failed to mark notification as read');
    return notificationId;
  }
);

export const markAllAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (userId: string) => {
    const response = await fetch(`/api/users/${userId}/notifications/read-all`, {
      method: 'PUT',
    });
    if (!response.ok) throw new Error('Failed to mark all notifications as read');
    return response.json();
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async (notificationId: string) => {
    const response = await fetch(`/api/notifications/${notificationId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete notification');
    return notificationId;
  }
);

// Slice
const initialState: NotificationsState & ReturnType<typeof notificationsAdapter.getInitialState> = {
  ...notificationsAdapter.getInitialState(),
  loading: false,
  error: null,
  unreadCount: 0,
  lastFetch: null,
  soundEnabled: true,
  desktopEnabled: false,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      notificationsAdapter.addOne(state, action.payload);
      if (!action.payload.read) {
        state.unreadCount += 1;
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      const notification = state.entities[action.payload];
      if (notification && !notification.read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      notificationsAdapter.removeOne(state, action.payload);
    },
    toggleSound: (state) => {
      state.soundEnabled = !state.soundEnabled;
    },
    toggleDesktop: (state) => {
      state.desktopEnabled = !state.desktopEnabled;
    },
    updateUnreadCount: (state) => {
      const notifications = Object.values(state.entities).filter(Boolean) as Notification[];
      state.unreadCount = notifications.filter((n) => !n.read).length;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        notificationsAdapter.setAll(state, action.payload);
        state.lastFetch = new Date().toISOString();
        const notifications = action.payload as Notification[];
        state.unreadCount = notifications.filter((n) => !n.read).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch notifications';
      })
      // Mark as read
      .addCase(markAsRead.fulfilled, (state, action) => {
        notificationsAdapter.updateOne(state, {
          id: action.payload,
          changes: { read: true },
        });
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      // Mark all as read
      .addCase(markAllAsRead.fulfilled, (state) => {
        const updates = Object.keys(state.entities).map((id) => ({
          id,
          changes: { read: true },
        }));
        notificationsAdapter.updateMany(state, updates);
        state.unreadCount = 0;
      })
      // Delete notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const notification = state.entities[action.payload];
        if (notification && !notification.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        notificationsAdapter.removeOne(state, action.payload);
      })
      // Fetch notifications from DB
      .addCase(fetchNotificationsFromDB.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationsFromDB.fulfilled, (state, action) => {
        state.loading = false;
        // Add all notifications to state
        notificationsAdapter.setAll(state, action.payload);
        // Calculate unread count
        state.unreadCount = action.payload.filter((n) => !n.read).length;
        state.lastFetch = new Date().toISOString();
      })
      .addCase(fetchNotificationsFromDB.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch notifications';
      });
  },
});

// Actions
export const {
  addNotification,
  removeNotification,
  toggleSound,
  toggleDesktop,
  updateUnreadCount,
} = notificationsSlice.actions;

// Selectors
const notificationsSelectors = notificationsAdapter.getSelectors(
  (state: RootState) => state.notifications
);

export const selectAllNotifications = notificationsSelectors.selectAll;
export const selectNotificationById = notificationsSelectors.selectById;
export const selectNotificationsTotal = notificationsSelectors.selectTotal;

export const selectUnreadNotifications = (state: RootState) =>
  selectAllNotifications(state).filter((n) => !n.read);

export const selectNotificationsByType = (state: RootState, type: Notification['type']) =>
  selectAllNotifications(state).filter((n) => n.type === type);

export const selectHighPriorityNotifications = (state: RootState) =>
  selectAllNotifications(state).filter((n) => n.priority === 'high' && !n.read);

export const selectNotificationSettings = (state: RootState) => ({
  soundEnabled: state.notifications.soundEnabled,
  desktopEnabled: state.notifications.desktopEnabled,
});

export const selectNotificationStats = (state: RootState) => ({
  total: selectNotificationsTotal(state),
  unread: state.notifications.unreadCount,
  loading: state.notifications.loading,
  error: state.notifications.error,
  lastFetch: state.notifications.lastFetch,
});

// Reducer
export default notificationsSlice.reducer;
